import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  LyftContextProvider,
  SocketLockStatus,
  SocketLockStatusItem,
} from '@src/context/LyftContext';
import LyftSocketService from './LyftSocketService';
import { logger } from '@utils/logger';
import { AlertModal } from '@provider/AlertProvider';
import { useUnLockSegmentMutation } from '@store/rtk/rides';
import { selectUsers } from '@store/slices/users/usersSlice';
import { useAppSelector } from '@store/hooks';

const lockTypes = [
  'segment.locked',
  'segment.unlocked',
  'ride.locked',
  'ride.unlocked',
];
const contractorLockTypes = ['contractor.locked', 'contractor.unlocked'];

const UNLOCK_SYNC_PREFIX = 'lyft-unlock-sync:';
const COUNTDOWN_SECONDS = 30;

type UnlockSyncState = {
  deadline: number;
  resolved: boolean;
  unlocked?: boolean;
};

const unlockSyncKey = (segmentId: number | string) =>
  `${UNLOCK_SYNC_PREFIX}${segmentId}`;

const readUnlockSync = (segmentId: number | string): UnlockSyncState | null => {
  try {
    const raw = localStorage.getItem(unlockSyncKey(segmentId));
    return raw ? (JSON.parse(raw) as UnlockSyncState) : null;
  } catch {
    return null;
  }
};

const writeUnlockSync = (
  segmentId: number | string,
  state: UnlockSyncState
) => {
  try {
    localStorage.setItem(unlockSyncKey(segmentId), JSON.stringify(state));
  } catch {}
};

const clearUnlockSync = (segmentId: number | string) => {
  try {
    localStorage.removeItem(unlockSyncKey(segmentId));
  } catch {}
};

export const LyftProvider: FC<PropsWithChildren> = ({ children }) => {
  const [socketStatus, setSocketStatus] = useState({});
  const [socketContractorStatus, setSocketContractorStatus] = useState({});
  const { userInfo } = useAppSelector(selectUsers);
  const [socketLockStatus, setSocketLockStatus] = useState<SocketLockStatus>(
    {}
  );
  const [unlockResolvedSegment, setUnlockResolvedSegment] =
    useState<import('@src/context/LyftContext').UnlockResolvedSegment>(null);
  const [unLockSegment] = useUnLockSegmentMutation();

  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const activeResolverRef = useRef<{
    segmentId: number | string;
    onExternalResolve: () => void;
  } | null>(null);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const removeStatusById = (segmentId: number) => {
    setSocketStatus((prev) => {
      if (segmentId === undefined || segmentId === null) return prev as any;
      const next = { ...(prev as any) } as any;
      delete next[segmentId as unknown as string];
      logger.log('[Lyft] socketStatus removed segment', segmentId);
      return next as any;
    });
  };

  const stopCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    activeResolverRef.current = null;
  };

  useEffect(() => {
    if (userInfo) {
      LyftSocketService.start();
    } else {
      LyftSocketService.stop();
    }
  }, [userInfo]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e.key || !e.key.startsWith(UNLOCK_SYNC_PREFIX) || !e.newValue) {
        return;
      }
      const segmentId = e.key.slice(UNLOCK_SYNC_PREFIX.length);
      const state: UnlockSyncState = JSON.parse(e.newValue);
      if (!state.resolved) return;

      if (
        activeResolverRef.current &&
        String(activeResolverRef.current.segmentId) === segmentId
      ) {
        activeResolverRef.current.onExternalResolve();
      }

      if (state.unlocked) {
        setUnlockResolvedSegment({ segmentId, ts: Date.now() });
      }
    };
    window.addEventListener('storage', onStorage);

    const off = LyftSocketService.onMessage((parsedData, channel) => {
      if (
        channel === '/topic/unlock-ride' &&
        parsedData.lockedByUserId &&
        userInfoRef.current?.id === parsedData.lockedByUserId
      ) {
        stopCountdown();
        const segmentId = parsedData.segmentId;
        let resolvedLocally = false;

        const now = Date.now();
        const existing = readUnlockSync(segmentId);
        const deadline =
          existing && !existing.resolved && existing.deadline > now
            ? existing.deadline
            : now + COUNTDOWN_SECONDS * 1000;

        if (!existing || existing.resolved || existing.deadline <= now) {
          writeUnlockSync(segmentId, { deadline, resolved: false });
        }

        const callUnlockEndpoint = async (segmentIds: number[]) => {
          try {
            await unLockSegment({ body: { segmentIds } }).unwrap();
            logger.log('[Lyft] segment unlocked', segmentId);
          } catch (err) {
            logger.log('[Lyft] failed to unlock segment', err);
          }
        };

        const getSecondsLeft = () =>
          Math.max(0, Math.ceil((deadline - Date.now()) / 1000));

        const renderModal = () => {
          const secondsLeft = getSecondsLeft();
          AlertModal.show({
            title: 'Lock Override Request',
            message: `${parsedData.userName} is requesting access to edit this ride segment. Unlock it now?`,
            extraMessages: [
              `${parsedData.dateOfService} ● ${parsedData.memberName} ● ${parsedData.confirmationCode}`,
              `${secondsLeft} second${secondsLeft === 1 ? '' : 's'} remaining`,
            ],
            extraMessageStyle: { textAlign: 'center' },
            buttons: [
              {
                title: 'No',
                color: '#C6470FFF',
                onClick: () => resolve(false),
              },
              {
                title: 'Yes',
                color: '#4F7EFFFF',
                onClick: () => resolve(true),
              },
            ],
          });
        };

        const resolve = async (shouldCallEndpoint: boolean) => {
          if (resolvedLocally) return;
          resolvedLocally = true;
          stopCountdown();

          const alreadyResolvedElsewhere = readUnlockSync(segmentId)?.resolved;
          writeUnlockSync(segmentId, {
            deadline,
            resolved: true,
            unlocked: shouldCallEndpoint,
          });

          AlertModal.hide();

          if (shouldCallEndpoint && !alreadyResolvedElsewhere) {
            await callUnlockEndpoint([segmentId]);
            setUnlockResolvedSegment({ segmentId, ts: Date.now() });
          }

          setTimeout(() => clearUnlockSync(segmentId), 2000);
        };

        activeResolverRef.current = {
          segmentId,
          onExternalResolve: () => {
            if (resolvedLocally) return;
            resolvedLocally = true;
            stopCountdown();
            AlertModal.hide();
          },
        };

        renderModal();

        countdownIntervalRef.current = setInterval(() => {
          const current = readUnlockSync(segmentId);
          if (current?.resolved) {
            resolvedLocally = true;
            stopCountdown();
            AlertModal.hide?.();
            return;
          }
          if (getSecondsLeft() <= 0) {
            resolve(true);
            return;
          }
          renderModal();
        }, 500);
      }
      if (
        channel === '/topic/rides-refresh' &&
        lockTypes.includes(parsedData.type)
      ) {
        setSocketLockStatus((prev) => {
          const rideOrSegmentId =
            (parsedData.segmentId as string | number) ||
            (parsedData.rideId as string | number);

          if (rideOrSegmentId === undefined || rideOrSegmentId === null) {
            return prev as any;
          }

          const baseValue = {
            type: parsedData.type,
            isLocked:
              parsedData.type === 'segment.locked' ||
              parsedData.type === 'ride.locked',
            lockedBy: String(parsedData.userName ?? ''),
            userName: parsedData.userName,
            userId: Number.isFinite(Number(parsedData.userId))
              ? Number(parsedData.userId)
              : null,
          };

          const standingOrderRideIds = parsedData.standingOrderRideIds ?? [];

          return {
            ...prev,
            [rideOrSegmentId]: {
              ...baseValue,
              isStandingOrderRide: false,
            },

            ...Object.fromEntries(
              standingOrderRideIds.map((id) => [
                id,
                {
                  ...baseValue,
                  isStandingOrderRide: true,
                },
              ])
            ),
          } as SocketLockStatusItem;
        });
      }
      if (
        channel === '/topic/rides-refresh' &&
        contractorLockTypes.includes(parsedData.type)
      ) {
        let Enum: string = '';
        if (parsedData.type === 'contractor.locked' && parsedData.accepted) {
          Enum = 'Accepted';
        }
        if (parsedData.type === 'contractor.locked' && !parsedData.accepted) {
          Enum = 'Locked';
        }
        if (parsedData.type === 'contractor.unlocked') {
          Enum = 'Unlocked';
        }
        setSocketContractorStatus((prev) => {
          const contractorId = parsedData.contractorId as string | number;
          if (contractorId === undefined || contractorId === null)
            return prev as any;
          const nextVal = {
            type: parsedData.type,
            isLocked: Enum,
            lockedBy: String(parsedData.userName ?? ''),
            userId: Number.isFinite(Number(parsedData.userId))
              ? Number(parsedData.userId)
              : null,
          };

          return { ...(prev as any), [contractorId]: nextVal } as any;
        });
      }
      if (channel !== '/topic/lyft-ride-status') return;
      setSocketStatus((prev) => {
        const segId = parsedData.segmentId as string | number;
        if (segId === undefined || segId === null) return prev as any;

        const nextVal = {
          status: parsedData?.full_payload?.status || parsedData.status,
          statusId: parsedData.statusId,
          error: parsedData.message,
          rideId: parsedData.rideId,
          fullInfo: parsedData?.full_payload,
        };

        const prevVal = (prev as any)[segId];
        const unchanged =
          prevVal &&
          prevVal.status === nextVal.status &&
          prevVal.statusId === nextVal.statusId &&
          prevVal.error === nextVal.error &&
          prevVal.rideId === nextVal.rideId &&
          JSON.stringify(prevVal.fullInfo) === JSON.stringify(nextVal.fullInfo);
        if (unchanged) return prev as any;

        return { ...(prev as any), [segId]: nextVal } as any;
      });
    });

    return () => {
      off();
      stopCountdown();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const value = useMemo(() => {
    return {
      socketStatus,
      setSocketStatus,
      removeStatusById,
      socketLockStatus,
      setSocketLockStatus,
      socketContractorStatus,
      setSocketContractorStatus,
      unlockResolvedSegment,
      setUnlockResolvedSegment,
    };
  }, [
    socketStatus,
    setSocketStatus,
    removeStatusById,
    socketLockStatus,
    setSocketLockStatus,
    socketContractorStatus,
    setSocketContractorStatus,
    unlockResolvedSegment,
    setUnlockResolvedSegment,
  ]);

  return <LyftContextProvider value={value}>{children}</LyftContextProvider>;
};
